import React, { useEffect, useMemo, useState } from "react";
import { Domain } from "Domain";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Snackbar from "components/Snackbar/Snackbar.js";
import AddAlert from "@material-ui/icons/AddAlert";
import axios from "axios";
import Danger from "components/Typography/Danger";
import LoadingOverlay from "react-loading-overlay";
import EmptyTable from "components/EmptyTable";
import SingleSelect from "components/SingleSelect";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MuiButton from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import { PencilSimple, Plus, X } from "@phosphor-icons/react";

const emptyForm = {
  Id: 0,
  ClientChargeId: 0,
  ModalityName: "",
  ClientId: "",
  ClientName: "",
  DoctorId: "",
  DoctorName: "",
  ClientNightCharge: "",
  ClientNightStartTime: "",
  ClientNightEndTime: "",
  DoctorNightCharge: "",
  DoctorNightStartTime: "",
  DoctorNightEndTime: "",
  Status: "Active",
};

function formatTime(value) {
  return value ? String(value).slice(0, 5) : "";
}

function getSelectValue(option) {
  return option && option.Id && option.Label
    ? { value: option.Id, label: option.Label }
    : null;
}

const createDoctorRule = (values = {}) => ({
  key: values.key || Date.now() + Math.random(),
  DoctorId: values.DoctorId || "",
  DoctorName: values.DoctorName || "",
  DoctorNightCharge: values.DoctorNightCharge || "",
  DoctorNightStartTime: values.DoctorNightStartTime || "",
  DoctorNightEndTime: values.DoctorNightEndTime || "",
});

const createClientRule = (values = {}) => ({
  key: values.key || Date.now() + Math.random(),
  ClientId: values.ClientId || "",
  ClientName: values.ClientName || "",
  ClientNightCharge: values.ClientNightCharge || "",
  ClientNightStartTime: values.ClientNightStartTime || "",
  ClientNightEndTime: values.ClientNightEndTime || "",
});

const timeLabelProps = {
  shrink: true,
  style: {
    color: "#64748b",
    fontWeight: 700,
    fontSize: 13,
  },
};

const compactControlProps = {
  fullWidth: true,
  style: {
    marginTop: 4,
    marginBottom: 4,
  },
};

const ruleGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md:
      "minmax(190px, 1.25fr) minmax(115px, 0.75fr) minmax(115px, 0.75fr) minmax(115px, 0.75fr) 72px",
  },
  gap: 1.4,
  alignItems: "center",
  mb: 1.2,
  "& .css-yk16xz-control, & .css-1pahdxg-control": {
    minHeight: "40px",
  },
  "& .css-g1d714-ValueContainer": {
    paddingTop: 0,
    paddingBottom: 0,
  },
};

const actionButtonSx = {
  width: 38,
  height: 38,
  borderRadius: "8px",
};

const addButtonSx = {
  ...actionButtonSx,
  color: "#ffffff",
  background: "#2f72f2",
  "&:hover": { background: "#1d5fce" },
};

const removeButtonSx = {
  ...actionButtonSx,
  color: "#64748b",
};

export default function Modalities() {
  const [saved, setSaved] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [edit, setEdit] = useState("");
  const [modalities, setModalities] = useState([]);
  const [clients, setClients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [validated, setValidated] = useState(true);
  const [loading, setLoading] = useState(true);
  const [empty, setEmpty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState(emptyForm);
  const [doctorRules, setDoctorRules] = useState([createDoctorRule()]);
  const [clientRules, setClientRules] = useState([createClientRule()]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const clientOptions = clients.map((client) => ({
    value: client.Id,
    label: client.Name,
  }));
  const doctorOptions = doctors.map((doctor) => ({
    value: doctor.Id,
    label: doctor.Name,
  }));

  const modalityRows = useMemo(
    () =>
      modalities.map((item) => ({
        ...item,
        RowId: `${item.Id}-${item.ClientChargeId || 0}`,
      })),
    [modalities]
  );

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return modalityRows.slice(start, start + rowsPerPage);
  }, [modalityRows, page, rowsPerPage]);

  const showSavedNotification = () => {
    if (!saved) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const showDeletedNotification = () => {
    if (!deleted) {
      setDeleted(true);
      setTimeout(() => setDeleted(false), 3000);
    }
  };

  function handleData(e) {
    setData({ ...data, [e.target.id]: e.target.value });
  }

  function updateDoctorRule(index, values) {
    setDoctorRules((rules) =>
      rules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...values } : rule
      )
    );
  }

  function updateClientRule(index, values) {
    setClientRules((rules) =>
      rules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...values } : rule
      )
    );
  }

  function handleDoctorField(index, field, value) {
    updateDoctorRule(index, { [field]: value });
  }

  function handleClientField(index, field, value) {
    updateClientRule(index, { [field]: value });
  }

  function handleDoctorSelect(index, selected) {
    updateDoctorRule(index, {
      DoctorId: selected.Id,
      DoctorName: selected.Label,
    });
  }

  function handleClientSelect(index, selected) {
    updateClientRule(index, {
      ClientId: selected.Id,
      ClientName: selected.Label,
    });
  }

  function addDoctorRule() {
    setDoctorRules((rules) => [...rules, createDoctorRule()]);
  }

  function addClientRule() {
    setClientRules((rules) => [...rules, createClientRule()]);
  }

  function removeDoctorRule(index) {
    setDoctorRules((rules) =>
      rules.filter((_rule, ruleIndex) => ruleIndex !== index)
    );
  }

  function removeClientRule(index) {
    setClientRules((rules) =>
      rules.filter((_rule, ruleIndex) => ruleIndex !== index)
    );
  }

  function handleClear() {
    setData(emptyForm);
    setDoctorRules([createDoctorRule()]);
    setClientRules([createClientRule()]);
    setEdit("");
  }

  function handleCloseModal() {
    handleClear();
    setValidated(true);
    setSaving(false);
    setOpenModal(false);
  }

  function validateFields() {
    if (data.ModalityName === "") return false;
    if (!clientRules.length || !doctorRules.length) return false;
    if (
      clientRules.some(
        (rule) =>
          !rule.ClientId ||
          rule.ClientNightCharge === "" ||
          !rule.ClientNightStartTime ||
          !rule.ClientNightEndTime
      )
    ) {
      return false;
    }
    if (
      doctorRules.some(
        (rule) =>
          !rule.DoctorId ||
          rule.DoctorNightCharge === "" ||
          !rule.DoctorNightStartTime ||
          !rule.DoctorNightEndTime
      )
    ) {
      return false;
    }
    return true;
  }

  function fetchModalities() {
    let formData = new FormData();
    formData.append("Status", "Active");
    setLoading(true);
    axios({
      method: "post",
      url: Domain + "/GetAllModalities.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        if (response.data.TotalCount != 0) {
          setModalities(response.data.data);
          setEmpty(false);
        } else {
          setModalities([]);
          setEmpty(true);
        }
        setLoading(false);
      })
      .catch((response) => {
        showDeletedNotification();
        setLoading(false);
        console.log(response);
      });
  }

  function fetchClients() {
    let formData = new FormData();
    formData.append("Status", "Active");
    axios({
      method: "post",
      url: Domain + "/GetAllClients.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        setClients(
          response.data.success && response.data.TotalCount > 0
            ? response.data.data
            : []
        );
      })
      .catch((response) => console.log(response));
  }

  function fetchDoctors() {
    let formData = new FormData();
    formData.append("Status", "Active");
    axios({
      method: "post",
      url: Domain + "/GetAllDoctors.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        setDoctors(
          response.data.success && response.data.TotalCount > 0
            ? response.data.data
            : []
        );
      })
      .catch((response) => console.log(response));
  }

  function handleSave() {
    if (!validateFields()) {
      setValidated(false);
      return;
    }

    setValidated(true);
    setSaving(true);

    const requests = clientRules.flatMap((clientRule, clientIndex) =>
      doctorRules.map((doctorRule, doctorIndex) => {
        let formData = new FormData();
        const isFirstRule = clientIndex === 0 && doctorIndex === 0;
        formData.append("Id", isFirstRule ? data.Id || 0 : 0);
        formData.append(
          "ClientChargeId",
          isFirstRule ? data.ClientChargeId || 0 : 0
        );
        formData.append("ModalityName", data.ModalityName);
        formData.append("ClientId", clientRule.ClientId);
        formData.append("DoctorId", doctorRule.DoctorId);
        formData.append("DocNightchrg", doctorRule.DoctorNightCharge);
        formData.append("DocNightStartTime", doctorRule.DoctorNightStartTime);
        formData.append("DocNightEndTime", doctorRule.DoctorNightEndTime);
        formData.append("ClNightChrg", clientRule.ClientNightCharge);
        formData.append("ClNightStartTime", clientRule.ClientNightStartTime);
        formData.append("ClNightEndTime", clientRule.ClientNightEndTime);
        formData.append("Status", data.Status || "Active");

        return axios({
          method: "post",
          url: Domain + "/SaveModality.php",
          data: formData,
          config: { headers: { "Content-Type": "multipart/form-data" } },
        });
      })
    );

    Promise.all(requests)
      .then((responses) => {
        if (responses.every((response) => response.data.success)) {
          handleClear();
          setEmpty(false);
          showSavedNotification();
          setOpenModal(false);
          fetchModalities();
        } else {
          setEmpty(true);
        }
      })
      .catch((response) => {
        showDeletedNotification();
        console.log(response);
      })
      .finally(() => setSaving(false));
  }

  useEffect(() => {
    fetchModalities();
    fetchClients();
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (edit !== "") {
      const selectedModality = modalityRows.find((item) => item.RowId === edit);
      if (selectedModality) {
        setData({
          ...emptyForm,
          ...selectedModality,
          ClientNightStartTime:
            formatTime(selectedModality.ClientNightStartTime) ||
            (selectedModality.ClientNightCharge ? "21:00" : ""),
          ClientNightEndTime:
            formatTime(selectedModality.ClientNightEndTime) ||
            (selectedModality.ClientNightCharge ? "08:00" : ""),
          DoctorNightStartTime:
            formatTime(selectedModality.DoctorNightStartTime) ||
            (selectedModality.DoctorNightCharge ? "22:00" : ""),
          DoctorNightEndTime:
            formatTime(selectedModality.DoctorNightEndTime) ||
            (selectedModality.DoctorNightCharge ? "07:00" : ""),
        });
        setClientRules([
          createClientRule({
            ClientId: selectedModality.ClientId,
            ClientName: selectedModality.ClientName,
            ClientNightCharge: selectedModality.ClientNightCharge,
            ClientNightStartTime:
              formatTime(selectedModality.ClientNightStartTime) ||
              (selectedModality.ClientNightCharge ? "21:00" : ""),
            ClientNightEndTime:
              formatTime(selectedModality.ClientNightEndTime) ||
              (selectedModality.ClientNightCharge ? "08:00" : ""),
          }),
        ]);
        setDoctorRules([
          createDoctorRule({
            DoctorId: selectedModality.DoctorId,
            DoctorName: selectedModality.DoctorName,
            DoctorNightCharge: selectedModality.DoctorNightCharge,
            DoctorNightStartTime:
              formatTime(selectedModality.DoctorNightStartTime) ||
              (selectedModality.DoctorNightCharge ? "22:00" : ""),
            DoctorNightEndTime:
              formatTime(selectedModality.DoctorNightEndTime) ||
              (selectedModality.DoctorNightCharge ? "07:00" : ""),
          }),
        ]);
        setOpenModal(true);
      }
    }
  }, [edit, modalityRows]);

  return (
    <>
      <Snackbar
        place="bc"
        color="success"
        icon={AddAlert}
        message="Modality Saved Successfully"
        open={saved}
        closeNotification={() => setSaved(false)}
        close
      />
      <Snackbar
        place="bc"
        color="danger"
        icon={AddAlert}
        message="Failed. Try again"
        open={deleted}
        closeNotification={() => setDeleted(false)}
        close
      />
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        BackdropProps={{
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
        PaperProps={{ sx: { borderRadius: "8px", maxHeight: "92vh" } }}
      >
        <LoadingOverlay active={saving} spinner text="Saving Please Wait..">
          <Box
            sx={{
              px: 2.5,
              py: 1.8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #e8eef7",
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: 18, fontWeight: 900, color: "#162033" }}
              >
                {edit ? "Edit Modality" : "Add Modality"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#7b8798", mt: 0.3 }}>
                Configure doctor night charges and client-specific rules.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: 2,
              }}
            >
              <MuiButton
                size="small"
                variant="outlined"
                onClick={handleClear}
                sx={{
                  minWidth: 64,
                  height: 34,
                  borderColor: "#cbd5e1",
                  color: "#475569",
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#94a3b8",
                    background: "#f8fafc",
                  },
                }}
              >
                Clear
              </MuiButton>
              <MuiButton
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  minWidth: 64,
                  height: 34,
                  background: "#2f72f2",
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: "none",
                  boxShadow: "0 6px 14px rgba(47,114,242,0.22)",
                  "&:hover": { background: "#1d5fce" },
                }}
              >
                Save
              </MuiButton>
              <IconButton
                onClick={handleCloseModal}
                sx={{ width: 34, height: 34, borderRadius: "8px" }}
              >
                <X size={20} weight="bold" />
              </IconButton>
            </Box>
          </Box>
          <form>
            <CardBody>
              <Box sx={{ px: { xs: 0, md: 0.5 }, py: 0 }}>
                <Box sx={{ maxWidth: 360, mb: 1.8 }}>
                  <CustomInput
                    onChange={handleData}
                    value={data.ModalityName}
                    labelText="Modality Name"
                    id="ModalityName"
                    formControlProps={compactControlProps}
                  />
                </Box>

                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 900,
                    color: "#64748b",
                    mb: 0.7,
                  }}
                >
                  Doctor Adding Details
                </Typography>
                {doctorRules.map((rule, index) => (
                  <Box key={rule.key} sx={ruleGridSx}>
                    <SingleSelect
                      selectedVal={getSelectValue({
                        Id: rule.DoctorId,
                        Label: rule.DoctorName,
                      })}
                      noOptionsMessage="No doctors"
                      placeholder="Select Doctor"
                      Options={doctorOptions}
                      setValue={(selected) =>
                        handleDoctorSelect(index, selected)
                      }
                      formControlProps={compactControlProps}
                    />
                    <CustomInput
                      onChange={(event) =>
                        handleDoctorField(
                          index,
                          "DoctorNightCharge",
                          event.target.value
                        )
                      }
                      value={rule.DoctorNightCharge}
                      labelText="Doctor Night Charge"
                      id={`DoctorNightCharge-${index}`}
                      formControlProps={compactControlProps}
                    />
                    <CustomInput
                      onChange={(event) =>
                        handleDoctorField(
                          index,
                          "DoctorNightStartTime",
                          event.target.value
                        )
                      }
                      value={rule.DoctorNightStartTime}
                      labelText="Doctor Night From"
                      id={`DoctorNightStartTime-${index}`}
                      type="time"
                      labelProps={timeLabelProps}
                      inputProps={{ step: 60 }}
                      formControlProps={compactControlProps}
                    />
                    <CustomInput
                      onChange={(event) =>
                        handleDoctorField(
                          index,
                          "DoctorNightEndTime",
                          event.target.value
                        )
                      }
                      value={rule.DoctorNightEndTime}
                      labelText="Doctor Night To"
                      id={`DoctorNightEndTime-${index}`}
                      type="time"
                      labelProps={timeLabelProps}
                      inputProps={{ step: 60 }}
                      formControlProps={compactControlProps}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                      }}
                    >
                      {doctorRules.length > 1 && (
                        <Tooltip title="Remove doctor row">
                          <IconButton
                            onClick={() => removeDoctorRule(index)}
                            sx={removeButtonSx}
                          >
                            <X size={18} weight="bold" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {index === doctorRules.length - 1 && (
                        <Tooltip title="Add doctor row">
                          <IconButton onClick={addDoctorRule} sx={addButtonSx}>
                            <Plus size={18} weight="bold" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}

                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 900,
                    color: "#64748b",
                    mt: 1.8,
                    mb: 0.7,
                  }}
                >
                  Client Adding Detail
                </Typography>
                {clientRules.map((rule, index) => (
                  <Box key={rule.key} sx={ruleGridSx}>
                    <SingleSelect
                      selectedVal={getSelectValue({
                        Id: rule.ClientId,
                        Label: rule.ClientName,
                      })}
                      noOptionsMessage="No clients"
                      placeholder="Select Client"
                      Options={clientOptions}
                      setValue={(selected) =>
                        handleClientSelect(index, selected)
                      }
                      formControlProps={compactControlProps}
                    />
                    <CustomInput
                      onChange={(event) =>
                        handleClientField(
                          index,
                          "ClientNightCharge",
                          event.target.value
                        )
                      }
                      value={rule.ClientNightCharge}
                      labelText="Client Night Charge"
                      id={`ClientNightCharge-${index}`}
                      formControlProps={compactControlProps}
                    />
                    <CustomInput
                      onChange={(event) =>
                        handleClientField(
                          index,
                          "ClientNightStartTime",
                          event.target.value
                        )
                      }
                      value={rule.ClientNightStartTime}
                      labelText="Client Night From"
                      id={`ClientNightStartTime-${index}`}
                      type="time"
                      labelProps={timeLabelProps}
                      inputProps={{ step: 60 }}
                      formControlProps={compactControlProps}
                    />
                    <CustomInput
                      onChange={(event) =>
                        handleClientField(
                          index,
                          "ClientNightEndTime",
                          event.target.value
                        )
                      }
                      value={rule.ClientNightEndTime}
                      labelText="Client Night To"
                      id={`ClientNightEndTime-${index}`}
                      type="time"
                      labelProps={timeLabelProps}
                      inputProps={{ step: 60 }}
                      formControlProps={compactControlProps}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                      }}
                    >
                      {clientRules.length > 1 && (
                        <Tooltip title="Remove client row">
                          <IconButton
                            onClick={() => removeClientRule(index)}
                            sx={removeButtonSx}
                          >
                            <X size={18} weight="bold" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {index === clientRules.length - 1 && (
                        <Tooltip title="Add client row">
                          <IconButton onClick={addClientRule} sx={addButtonSx}>
                            <Plus size={18} weight="bold" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
              {!validated && (
                <Danger>Please enter all the details to save</Danger>
              )}
            </CardBody>
          </form>
        </LoadingOverlay>
      </Dialog>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                borderBottom: "1px solid #e8eef7",
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: 22, fontWeight: 900, color: "#162033" }}
                >
                  Modalities
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#7b8798", mt: 0.3 }}>
                  Manage billing categories and client-specific night charges.
                </Typography>
              </Box>
              <MuiButton
                variant="contained"
                startIcon={<Plus size={17} weight="bold" />}
                onClick={() => setOpenModal(true)}
                sx={{
                  background: "#2f72f2",
                  color: "#ffffff",
                  boxShadow: "0 8px 18px rgba(47,114,242,0.22)",
                  fontWeight: 800,
                  px: 2,
                  py: 1,
                  "&:hover": { background: "#1d5fce" },
                }}
              >
                Add Modality
              </MuiButton>
            </Box>
            <CardBody>
              <LoadingOverlay active={loading} spinner text="Please Wait..">
                {empty ? (
                  <EmptyTable />
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #e8eef7",
                      overflow: "hidden",
                    }}
                  >
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {[
                              "ID",
                              "Modality",
                              "Client",
                              "Doctor",
                              "Doctor Charge",
                              "Doctor Time",
                              "Client Charge",
                              "Client Time",
                              "Status",
                              "",
                            ].map((head) => (
                              <TableCell
                                key={head}
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 800,
                                  color: "#64748b",
                                  background: "#fbfdff",
                                }}
                              >
                                {head}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedRows.map((row) => (
                            <TableRow key={row.RowId} hover>
                              <TableCell>{row.Id}</TableCell>
                              <TableCell>
                                <b>{row.ModalityName}</b>
                              </TableCell>
                              <TableCell>{row.ClientName || "-"}</TableCell>
                              <TableCell>{row.DoctorName || "-"}</TableCell>
                              <TableCell>{row.DoctorNightCharge}</TableCell>
                              <TableCell>
                                {formatTime(row.DoctorNightStartTime) || "-"} -{" "}
                                {formatTime(row.DoctorNightEndTime) || "-"}
                              </TableCell>
                              <TableCell>{row.ClientNightCharge}</TableCell>
                              <TableCell>
                                {formatTime(row.ClientNightStartTime) || "-"} -{" "}
                                {formatTime(row.ClientNightEndTime) || "-"}
                              </TableCell>
                              <TableCell>{row.Status}</TableCell>
                              <TableCell align="right">
                                <Tooltip title="Edit modality">
                                  <IconButton
                                    size="small"
                                    onClick={() => setEdit(row.RowId)}
                                    sx={{
                                      color: "#2f72f2",
                                      background: "#e8f0fe",
                                      borderRadius: "8px",
                                      "&:hover": { background: "#d8e7ff" },
                                    }}
                                  >
                                    <PencilSimple size={18} weight="duotone" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {modalityRows.length > 0 && (
                      <TablePagination
                        component="div"
                        count={modalityRows.length}
                        page={page}
                        onPageChange={(_event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                          setRowsPerPage(parseInt(event.target.value, 10));
                          setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                      />
                    )}
                  </Paper>
                )}
              </LoadingOverlay>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </>
  );
}
